import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Table from "../../../core/common/dataTable/index";
import { getHrmsApiLogData } from "../../../core/data/redux/hrmsApiLogsSlice";
import { all_routes } from "../../router/all_routes";
import { formatDateTime } from "../../../utils/dateUtils";

const HrmsApiLogs = () => {
    const routes = all_routes;
    const dispatch = useDispatch();
    const hrmsApiLogState = useSelector((state: any) => state.hrmsApiLogState);

    const [dataList, setDataList] = useState<any[]>([]);
    const [query, setQuery] = useState<string>("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 10,
    });

    const fetchRecords = (pagination: any, query: string) => {
        dispatch(
            getHrmsApiLogData({
                page: pagination.current,
                limit: pagination.pageSize,
            })
        );
    };

    const handleRefresh = () => {
        console.log("Refreshing HRMS API logs...");
        fetchRecords(pagination, query);
    };

    const handlePagination = (paginationEvent: any) => {
        const newPagination = {
            current: paginationEvent.current,
            pageSize: paginationEvent.pageSize,
            total: pagination.total,
        };
        setPagination(newPagination);
        fetchRecords(newPagination, query);
    };

    const handleSearch = (searchQuery: string) => {
        setQuery(searchQuery);
        const resetPagination = {
            ...pagination,
            current: 1,
        };
        setPagination(resetPagination);
        fetchRecords(resetPagination, searchQuery);
    };

    useEffect(() => {
        fetchRecords(pagination, query);
    }, []);

    useEffect(() => {
        if (
            hrmsApiLogState.loading === "fulfilled" &&
            hrmsApiLogState.action === "list"
        ) {
            console.log("HRMS API logs fetched successfully:", hrmsApiLogState);
            // Handle triple-nested data structure: data.data.data
            const logsData = hrmsApiLogState.data?.data?.data?.data ||
                hrmsApiLogState.data?.data?.data ||
                hrmsApiLogState.data?.data ||
                hrmsApiLogState.data ||
                [];
            setDataList(Array.isArray(logsData) ? logsData : []);
            setPagination((prev) => ({
                ...prev,
                total: hrmsApiLogState.data?.data?.data?.total ||
                    hrmsApiLogState.data?.data?.total ||
                    hrmsApiLogState.data?.total ||
                    0,
                pageSize: hrmsApiLogState.data?.data?.data?.limit ||
                    hrmsApiLogState.data?.data?.limit ||
                    hrmsApiLogState.data?.limit ||
                    prev.pageSize,
            }));
        } else if (hrmsApiLogState.loading === "rejected") {
            console.error("Error fetching HRMS API logs:", hrmsApiLogState?.error);
            toast.error(hrmsApiLogState?.error || "Failed to fetch HRMS API logs");
        }
    }, [hrmsApiLogState.loading, hrmsApiLogState.action]);

    const columns = [
        {
            title: "Sr. No.",
            dataIndex: "id",
            render: (text: string, record: any, index: number) => (
                <>
                    <div>
                        {pagination.current
                            ? index +
                            pagination.current * pagination.pageSize -
                            pagination.pageSize +
                            1
                            : index + 1}
                    </div>
                </>
            ),
            sorter: (a: any, b: any) => a.id - b.id,
        },
        {
            title: "Route",
            dataIndex: "route",
            render: (text: any) => {
                const displayText = typeof text === "object" && text !== null
                    ? JSON.stringify(text)
                    : text
                        ? String(text)
                        : "N/A";
                return (
                    <span style={{ fontSize: "12px", wordBreak: "break-word" }}>
                        {displayText}
                    </span>
                );
            },
            width: 250,
        },
        {
            title: "Method",
            dataIndex: "method",
            render: (text: any) => {
                const displayText = typeof text === "object" && text !== null
                    ? JSON.stringify(text)
                    : text
                        ? String(text)
                        : "N/A";
                return (
                    <span style={{ fontWeight: "500" }}>{displayText}</span>
                );
            },
            width: 100,
        },
        {
            title: "Request Body",
            dataIndex: "requestBody",
            width: 300,
            render: (text: any) => (
                <pre
                    style={{
                        backgroundColor: "#d4edda",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        maxHeight: "200px",
                        minHeight: "200px",
                        overflowY: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontSize: "12px",
                        color: "#155724"
                    }}
                >
                    {typeof text === "object"
                        ? JSON.stringify(text, null, 2)
                        : text
                            ? (() => {
                                try {
                                    return JSON.stringify(JSON.parse(text), null, 2);
                                } catch {
                                    return text || "";
                                }
                            })()
                            : "N/A"}
                </pre>
            ),
        },
        {
            title: "Request Query",
            dataIndex: "requestQuery",
            width: 200,
            render: (text: any) => {
                let displayText = "N/A";
                if (text === null || text === undefined) {
                    displayText = "N/A";
                } else if (typeof text === "object") {
                    displayText = Object.keys(text).length > 0
                        ? JSON.stringify(text, null, 2)
                        : "N/A";
                } else if (typeof text === "string") {
                    try {
                        const parsed = JSON.parse(text);
                        displayText = typeof parsed === "object" && Object.keys(parsed).length > 0
                            ? JSON.stringify(parsed, null, 2)
                            : text || "N/A";
                    } catch {
                        displayText = text || "N/A";
                    }
                } else {
                    displayText = String(text) || "N/A";
                }
                return (
                    <pre
                        style={{
                            backgroundColor: "#e7f3ff",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            maxHeight: "150px",
                            minHeight: "150px",
                            overflowY: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontSize: "12px",
                            color: "#004085"
                        }}
                    >
                        {displayText}
                    </pre>
                );
            },
        },
        {
            title: "Request Headers",
            dataIndex: "requestHeaders",
            width: 250,
            render: (text: any) => {
                let displayText = "N/A";
                if (text === null || text === undefined) {
                    displayText = "N/A";
                } else if (typeof text === "object") {
                    displayText = Object.keys(text).length > 0
                        ? JSON.stringify(text, null, 2)
                        : "N/A";
                } else if (typeof text === "string") {
                    try {
                        const parsed = JSON.parse(text);
                        displayText = typeof parsed === "object" && Object.keys(parsed).length > 0
                            ? JSON.stringify(parsed, null, 2)
                            : text || "N/A";
                    } catch {
                        displayText = text || "N/A";
                    }
                } else {
                    displayText = String(text) || "N/A";
                }
                return (
                    <pre
                        style={{
                            backgroundColor: "#fff3cd",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            maxHeight: "150px",
                            minHeight: "150px",
                            overflowY: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontSize: "12px",
                            color: "#856404"
                        }}
                    >
                        {displayText}
                    </pre>
                );
            },
        },
        {
            title: "Response Body",
            dataIndex: "responseBody",
            width: 300,
            render: (text: any) => {
                let jsonres: any = null;
                if (typeof text === "object") {
                    jsonres = text;
                } else if (text) {
                    try {
                        jsonres = JSON.parse(text);
                    } catch (e) {
                        jsonres = null;
                    }
                }

                const isSuccess = jsonres?.success ||
                    (typeof jsonres === "object" && jsonres !== null && !jsonres.error) ||
                    (!text || text === "null" || text === "undefined");

                return (
                    <pre
                        style={{
                            backgroundColor: isSuccess ? "#d4edda" : "#f8d7da",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            maxHeight: "200px",
                            minHeight: "200px",
                            overflowY: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontSize: "12px",
                            color: isSuccess ? "#155724" : "#721c24",
                        }}
                    >
                        {typeof text === "object"
                            ? JSON.stringify(text, null, 2)
                            : text && text !== "null" && text !== "undefined"
                                ? (() => {
                                    try {
                                        return JSON.stringify(JSON.parse(text), null, 2);
                                    } catch {
                                        return text || "N/A";
                                    }
                                })()
                                : "N/A"}
                    </pre>
                );
            },
        },
        {
            title: "Status Code",
            dataIndex: "statusCode",
            render: (text: any) => {
                const statusCode = typeof text === "number" ? text :
                    typeof text === "string" ? parseInt(text) :
                        typeof text === "object" && text !== null ? null :
                            null;
                const displayText = statusCode !== null ? String(statusCode) : "N/A";
                return (
                    <span style={{
                        color: statusCode !== null && statusCode >= 200 && statusCode < 300 ? "#28a745" :
                            statusCode !== null && statusCode >= 400 ? "#dc3545" :
                                statusCode !== null ? "#ffc107" : "#6c757d",
                        fontWeight: "500"
                    }}>
                        {displayText}
                    </span>
                );
            },
            width: 120,
        },
        {
            title: "SAP Request URL",
            dataIndex: "sapRequestUrl",
            render: (text: any) => {
                const displayText = typeof text === "object" && text !== null
                    ? JSON.stringify(text)
                    : text
                        ? String(text)
                        : "N/A";
                return (
                    <span style={{ fontSize: "12px", wordBreak: "break-word" }}>
                        {displayText}
                    </span>
                );
            },
            width: 300,
        },
        {
            title: "SAP Request Body",
            dataIndex: "sapRequestBody",
            width: 250,
            render: (text: any) => (
                <pre
                    style={{
                        backgroundColor: "#d1ecf1",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        maxHeight: "150px",
                        minHeight: "150px",
                        overflowY: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontSize: "12px",
                        color: "#0c5460"
                    }}
                >
                    {typeof text === "object" && text !== null
                        ? JSON.stringify(text, null, 2)
                        : text && text !== "null" && text !== "undefined"
                            ? (() => {
                                try {
                                    return JSON.stringify(JSON.parse(text), null, 2);
                                } catch {
                                    return text || "N/A";
                                }
                            })()
                            : "N/A"}
                </pre>
            ),
        },
        {
            title: "SAP Response Body",
            dataIndex: "sapResponseBody",
            width: 400,
            render: (text: any) => {
                let jsonres: any = null;
                if (typeof text === "object") {
                    jsonres = text;
                } else if (text) {
                    try {
                        jsonres = JSON.parse(text);
                    } catch (e) {
                        jsonres = null;
                    }
                }

                const isSuccess = jsonres?.d ||
                    (typeof jsonres === "object" && jsonres !== null && !jsonres.error) ||
                    (!text || text === "null" || text === "undefined");

                return (
                    <pre
                        style={{
                            backgroundColor: isSuccess ? "#d4edda" : "#f8d7da",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            maxHeight: "200px",
                            minHeight: "200px",
                            overflowY: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontSize: "12px",
                            color: isSuccess ? "#155724" : "#721c24",
                        }}
                    >
                        {typeof text === "object"
                            ? JSON.stringify(text, null, 2)
                            : text && text !== "null" && text !== "undefined"
                                ? (() => {
                                    try {
                                        return JSON.stringify(JSON.parse(text), null, 2);
                                    } catch {
                                        return text || "N/A";
                                    }
                                })()
                                : "N/A"}
                    </pre>
                );
            },
        },
        {
            title: "Created at",
            dataIndex: "created_at",
            render: (text: any) => {
                if (!text) return <span>N/A</span>;
                const dateText = typeof text === "object" && text !== null
                    ? JSON.stringify(text)
                    : String(text);
                try {
                    return <span>{formatDateTime(dateText)}</span>;
                } catch {
                    return <span>{dateText}</span>;
                }
            },
            width: 180,
        },
    ];

    return (
        <>
            <style>
                {`
          .spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
            </style>
            <div className="page-wrapper">
                <div className="content">
                    <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
                        <div className="my-auto mb-2">
                            <h3 className="page-title mb-1">HRMS API Logs</h3>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={routes.adminDashboard}>Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to="#">Logs</Link>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        HRMS API Logs
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                            <div className="mb-2">
                                <button
                                    className="btn btn-primary d-flex align-items-center"
                                    onClick={handleRefresh}
                                    disabled={hrmsApiLogState.loading === "pending"}
                                    title="Refresh HRMS API Logs"
                                >
                                    <i
                                        className={`ti ti-refresh me-2 ${hrmsApiLogState.loading === "pending" ? "spin" : ""
                                            }`}
                                    />
                                    {hrmsApiLogState.loading === "pending"
                                        ? "Refreshing..."
                                        : "Refresh"}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                            <h4 className="mb-4 mt-3">HRMS API Logs</h4>
                        </div>
                        <div className="card-body p-0 py-3">
                            <Table
                                columns={columns}
                                dataSource={dataList}
                                Selection={false}
                                paginationConfig={pagination}
                                handlePagination={handlePagination}
                                loading={
                                    hrmsApiLogState.loading === "pending" &&
                                    hrmsApiLogState.action === "list"
                                }
                                handleSearch={handleSearch}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default HrmsApiLogs;

